import { createSelector } from 'reselect'
import { filter, values, get, keys, flatMap, includes, isEmpty } from 'lodash'

import { getCurrentMenuId } from './appState'
import { getKioskBarcodeScanningEnabled, getSelectedMenusIds } from './config'
import { getCartLineItems } from './cart'
import { getCurrentVenue } from './venue'
import { getMenusById } from './menus'

const getKioskItems = (state) => state.items

export const getItemsForCurrentMenu = createSelector(
  getCurrentMenuId,
  getKioskItems,
  (currentMenuId, items) => items?.byMenuId?.[currentMenuId] || {},
)

const getModifierItemsForItem = (menuItem, menuItemsFromState) => {
  if (!menuItem.modifierGroups?.length) return []

  return menuItem.modifierGroups.map((group) => ({
    ...group,
    items: group.items?.map((item) => {
      if (!menuItemsFromState.byId[item.uuid]) {
        return item
      }
      return {
        ...menuItemsFromState.byId[item.uuid],
      }
    }),
  }))
}

// used for POS/QS/CFD to populate item modifier data
export const getPOSItemsForCurrentMenu = createSelector(getCurrentMenuId, getKioskItems, (currentMenuId, items) => {
  let menuItems = { ...get(items, ['byMenuId', currentMenuId], {}) }

  menuItems = {
    ...menuItems,
    byId: {
      ...menuItems.byId,
    },
  }

  for (let key in menuItems.byId) {
    if (!menuItems.byId[key].modifierGroups?.length) {
      continue
    }

    menuItems.byId[key] = {
      ...menuItems.byId[key],
      modifierGroups: getModifierItemsForItem(menuItems.byId[key], menuItems),
    }
  }

  return menuItems
})

export const getItemForCurrentMenuById = createSelector([getItemsForCurrentMenu, (_, id) => id], (currentMenuItems, id) => {
  const item = currentMenuItems?.byId?.[id] ?? {}

  if (!item.modifierGroups?.length) {
    return item
  }

  return {
    ...item,
    modifierGroups: getModifierItemsForItem(item, currentMenuItems),
  }
})

const getItemsValuesForCurrentMenu = createSelector(getPOSItemsForCurrentMenu, (currentMenuItems) =>
  values(get(currentMenuItems, 'byId', {})),
)

export const getCurrentItemValues = createSelector(getItemsForCurrentMenu, (currentMenuItems) =>
  Object.values(currentMenuItems?.byId ?? {}),
)

const getItemsIdsForCurrentMenu = createSelector(getItemsForCurrentMenu, (currentMenuItems) =>
  get(currentMenuItems, 'allIds', []),
)

export const getCurrentMenuItemsFilteredByCategory = (state, categoryId) => {
  const kioskBarcodeScanningEnabled = getKioskBarcodeScanningEnabled(state)
  const categoryItems = filter(getItemsValuesForCurrentMenu(state), (item) => includes(item.itemCategoryUuids, categoryId))

  if (!kioskBarcodeScanningEnabled) {
    return categoryItems
  }

  return categoryItems.filter((item) => !item.barcode)
}

export const getItemsImages = createSelector(getKioskItems, getSelectedMenusIds, getMenusById, (items, selectedMenuIds, menus) =>
  flatMap(selectedMenuIds, (id) => {
    if (!menus?.[id]?.configurationModelName?.toLowerCase().includes('belly-up')) {
      return []
    }
    const menuItems = values(get(items, ['byMenuId', id, 'byId']))
    return menuItems.map((item) => get(item, 'images.detail')).filter((image) => !!image)
  }),
)

export const getAreItemsLoaded = createSelector(getKioskItems, getSelectedMenusIds, (items, selectedIds) => {
  const itemKeys = keys(items.byMenuId)
  return selectedIds.every((id) => itemKeys.includes(id))
})

export const getItems = (state) => {
  const itemIds = getItemsIdsForCurrentMenu(state)
  const getItem = makeGetItemWithCartQuantity()
  const categories = get(getCurrentMenuCategories(state), 'byId', {})
  const items = itemIds.map((itemId) => {
    let item = getItem(state, itemId)
    item = {
      ...item,
      color: get(categories, `${item.itemCategoryUuid}.color`, null),
    }

    return item
  })

  return items
}

export const getMenuItems = createSelector(getItems, (items) => filter(items, ['modifier', false]))

// Return : An object containing the service charge rate; whether the service charge is taxable;
// the name of the service charge; and the legal text for the service charge.
const getMenuServiceChargeStringified = createSelector(
  [getItemsForCurrentMenu, getCurrentVenue],
  (currentMenuItems, currentVenue) => {
    const serviceChargeLegalText = currentVenue?.serviceChargeLegal
    const serviceFeeItems = get(currentMenuItems, 'serviceFeeItems', [])

    // Favor taxable service charges over non-taxable service charges.
    let service_charges = filter(serviceFeeItems, ['specialType', 'taxable_service_charge'])

    if (service_charges && !isEmpty(service_charges)) {
      return JSON.stringify({
        name: get(service_charges, '[0].name') || 'Service Charge',
        rate: get(service_charges, '[0].rate', 0),
        priceInCents: get(service_charges, '[0].variants[0].priceInCents', undefined),
        taxable: true,
        legal: serviceChargeLegalText,
        taxClassUuid: service_charges?.[0]?.taxClassUuid || null,
      })
    }

    service_charges = filter(serviceFeeItems, ['specialType', 'service_charge'])

    if (service_charges && !isEmpty(service_charges)) {
      return JSON.stringify({
        name: get(service_charges, '[0].name') || 'Service Charge',
        rate: get(service_charges, '[0].rate', 0),
        priceInCents: get(service_charges, '[0].variants[0].priceInCents', undefined),
        taxable: false,
        legal: serviceChargeLegalText,
      })
    }

    return JSON.stringify({ name: 'Service Charge', rate: 0, taxable: false, legal: null })
  },
)

export const getMenuServiceCharge = createSelector(getMenuServiceChargeStringified, (serviceCharge) => JSON.parse(serviceCharge))

export const makeGetMenuServiceCharge = () => createSelector(getMenuServiceCharge, (menuItems) => menuItems)

// this is causing the menu screen to re-render on every item change
// the cart should have a summary of the items and display quantities
const getItemWithCartQuantity = (state, id) => {
  const item = get(getItemsForCurrentMenu(state), ['byId', id], {})
  const cartItems = getCartLineItems(state)

  let cartQuantity = 0
  let cartQuantityWithModifiers = 0
  let cartIndex = -1

  const hasRequiredModifiers = item.modifierGroups?.find(
    ({ requiresSelection, min, defaults }) => requiresSelection || min > 0 || defaults?.length,
  )

  for (let i = 0; i < cartItems.length; i++) {
    if (cartItems[i].itemId !== id) continue

    if (hasRequiredModifiers) {
      cartQuantityWithModifiers += cartItems[i].quantity
      continue
    }

    const hasPriceAdjustment = cartItems[i].openPriceInCents !== null && cartItems[i].openPriceInCents !== undefined
    const hasNotesOrModifiers = cartItems[i].notes?.length || cartItems[i].modifiers?.length || hasPriceAdjustment

    if (hasNotesOrModifiers) {
      cartQuantityWithModifiers += cartItems[i].quantity
      continue
    }

    cartIndex = i
    cartQuantity += cartItems[i].quantity
  }

  return { ...item, cartQuantity, cartQuantityWithModifiers, hasRequiredModifiers, cartIndex }
}

export const makeGetItemWithCartQuantity = () => createSelector([getItemWithCartQuantity], (item) => item)

/**
 * There were issues testing memoized categories.js through getCurrentMenuCategories
 *
 * @param {State} state
 * @returns
 */
const getCurrentMenuCategories = (state) => {
  if (!state.appState?.currentMenuId) {
    return {}
  }
  return state.categories?.byMenuId?.[state.appState?.currentMenuId] || {}
}
